import React from 'react';
import { useTranslation } from 'react-i18next';

import { tzLabel } from '../../config/timezones';

/**
 * Shows the timezone that ACTUALLY applies, and where it came from.
 *
 * The resolution chain is  branch.timezone -> company.timezone -> Asia/Riyadh,
 * so a branch silently overrides the company setting. That precedence was
 * invisible on every screen that could set a timezone: an owner could set the
 * company zone in Profile (or an admin could set it in the admin panel) and it
 * would do nothing at all for any device attached to a branch, with no
 * indication anywhere. This component is the disclosure.
 *
 * `source` comes from the BACKEND (Device::timezoneSource) rather than being
 * re-derived here, so the UI can't drift from the real chain.
 *
 * @param {string}  timezone   the effective IANA zone
 * @param {string}  source     'branch' | 'company' | 'fallback'
 * @param {string}  sourceName branch name, when source === 'branch'
 * @param {boolean} compact    single-line, no icon
 */
export default function EffectiveTimezone({ timezone, source, sourceName, compact = false, className = '' }) {
    const { t } = useTranslation();
    if (!timezone) return null;

    const isFallback = source === 'fallback';

    const suffix = source === 'branch'
        ? t('timezone.src_branch', { name: sourceName || '', defaultValue: "from branch '{{name}}'" })
        : source === 'company'
            ? t('timezone.src_company', 'company setting')
            : t('timezone.src_fallback', 'default fallback (not configured)');

    return (
        <span
            className={`d-inline-flex align-items-center gap-1 ${className}`}
            style={{
                fontSize: compact ? 11 : 12,
                color: isFallback ? 'var(--pp-warning)' : 'var(--pp-text-secondary)',
            }}
            title={t('timezone.effective_title', 'The timezone this screen actually schedules in')}
        >
            {!compact && <i className="fa-regular fa-clock" />}
            <span style={{ fontWeight: 600 }}>{tzLabel(timezone)}</span>
            <span>— {suffix}</span>
        </span>
    );
}
